import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2, Paperclip } from 'lucide-react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { AddNewTradeDialog } from '@/shared/forms';
import { useTrades, useCreateTrade } from '@/features/punch-lists/hooks';
import { useRFIMutations, useProjectUsers } from '@/features/rfis/hooks';
import type {
  CreateRFIData,
  RFIPriority,
  RFIImpact,
  UserMinimal,
} from '@/features/rfis/types';
import { RFI_PRIORITY_LABELS, RFI_IMPACT_LABELS } from '@/features/rfis/types';
import { ViewPosition } from '../types/types';
import { useCreateHotspot } from '../hooks/useHotspotData';
import { AttachmentPhotoGallery, type AttachmentPhoto } from '@/shared/components/AttachmentPhotoGallery';
import type { PendingPhotoUpload } from './pendingPhotoUpload';

interface RfiHotspotFormProps {
  photoId: number;
  projectId: number | undefined;
  viewPosition: ViewPosition;
  hotspotId?: number;
  initialFiles?: File[];
  initialPreviewEntries?: Array<{
    id: string;
    file: File;
    previewUrl: string;
    isExternalPreview?: boolean;
  }>;
  pendingUploads?: PendingPhotoUpload[];
  onRemovePendingUpload?: (id: string) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

type FormState = {
  title: string;
  question: string;
  priority: RFIPriority;
  due_date: string;
  responsible_party_user: number | null;
  assignee_user: number | null;
  trade: string | null;
  cost_impact: RFIImpact;
  schedule_impact: RFIImpact;
  suggested_solution: string;
};

const INITIAL_FORM: FormState = {
  title: '',
  question: '',
  priority: 'NORMAL',
  due_date: '',
  responsible_party_user: null,
  assignee_user: null,
  trade: null,
  cost_impact: 'NONE',
  schedule_impact: 'NONE',
  suggested_solution: '',
};

const PRIORITIES = Object.keys(RFI_PRIORITY_LABELS) as RFIPriority[];
const IMPACTS = Object.keys(RFI_IMPACT_LABELS) as RFIImpact[];

const userDisplayName = (u: UserMinimal) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

export const RfiHotspotForm: React.FC<RfiHotspotFormProps> = ({
  photoId,
  projectId,
  viewPosition,
  hotspotId: existingHotspotId,
  initialFiles = [],
  initialPreviewEntries = [],
  pendingUploads = [],
  onRemovePendingUpload,
  onSuccess,
  onCancel,
}) => {
  const { createItem, uploadAttachment, linkAttachmentPhoto } = useRFIMutations(projectId);
  const { data: trades } = useTrades();
  const { data: projectUsers } = useProjectUsers(projectId);
  const createTrade = useCreateTrade();
  const createHotspotMutation = useCreateHotspot(
    projectId == null ? undefined : String(projectId),
  );

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [pendingFiles, setPendingFiles] = useState<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>(() => {
    if (pendingUploads.length > 0) return [];
    if (initialPreviewEntries.length > 0) {
      return initialPreviewEntries.map((entry) => ({ ...entry, isExternalPreview: true }));
    }
    return initialFiles.map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
      file,
      previewUrl: URL.createObjectURL(file),
      isExternalPreview: false,
    }));
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>([]);

  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setNullableUser = (key: 'responsible_party_user' | 'assignee_user', raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === 'none' ? null : Number(raw) }));

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      setPendingFiles((prev) => [
        ...prev,
        ...files.map((file, index) => ({
          id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
          file,
          previewUrl: URL.createObjectURL(file),
          isExternalPreview: false,
        })),
      ]);
    }
    e.target.value = '';
  };

  useEffect(() => () => {
    pendingFilesRef.current.forEach((entry) => {
      if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
    });
  }, []);

  const attachmentPreviews = useMemo<AttachmentPhoto[]>(
    () => [
      ...pendingUploads.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
      ...pendingFiles.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
    ],
    [pendingFiles, pendingUploads],
  );

  const removeFileById = (attachmentId: string) => {
    if (pendingUploads.some((entry) => entry.id === attachmentId)) {
      onRemovePendingUpload?.(attachmentId);
      return;
    }
    setPendingFiles((prev) => {
      const target = prev.find((entry) => entry.id === attachmentId);
      if (target && !target.isExternalPreview) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== attachmentId);
    });
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    set('trade', newTrade.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!projectId) {
      setError('No project context available.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      let hotspotId = existingHotspotId;

      if (!hotspotId) {
        const hotspotResponse = await createHotspotMutation.mutateAsync({
          photoId,
          position: {
            pitch: viewPosition.pitch,
            yaw: viewPosition.yaw,
            hfov: viewPosition.hfov,
          },
        });
        hotspotId = hotspotResponse.id;
      }

      const payload: CreateRFIData = {
        project: projectId,
        title: form.title.trim(),
        question: form.question.trim(),
        priority: form.priority,
        due_date: form.due_date ? form.due_date.slice(0, 10) : null,
        responsible_party_user: form.responsible_party_user,
        assignee_user: form.assignee_user,
        trade: form.trade || null,
        cost_impact: form.cost_impact,
        schedule_impact: form.schedule_impact,
        suggested_solution: form.suggested_solution.trim(),
        hotspot: hotspotId,
      };

      const newItem = await createItem.mutateAsync(payload);

      const settled = await Promise.allSettled(
        pendingUploads.map((entry) => entry.uploadPromise),
      );
      const linkOps: Promise<unknown>[] = [];
      settled.forEach((result, idx) => {
        const entry = pendingUploads[idx];
        if (result.status === 'fulfilled') {
          linkOps.push(
            linkAttachmentPhoto.mutateAsync({
              id: newItem.id,
              photoId: result.value.photoId,
            }),
          );
        } else {
          linkOps.push(
            uploadAttachment.mutateAsync({ id: newItem.id, file: entry.file }),
          );
        }
      });
      pendingFiles.forEach((attachment) => {
        linkOps.push(
          uploadAttachment.mutateAsync({ id: newItem.id, file: attachment.file }),
        );
      });
      await Promise.all(linkOps);

      setForm(INITIAL_FORM);
      pendingFiles.forEach((entry) => {
        if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
      });
      setPendingFiles([]);
      onSuccess();
    } catch {
      setError('Failed to create RFI. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-1">
      {/* Title */}
      <div className="space-y-1">
        <Label htmlFor="rfi-hs-title">
          Title <span className="text-destructive">*</span>
        </Label>
        <Input
          id="rfi-hs-title"
          placeholder="Example: Clarify fireproofing at stair core"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          autoFocus
        />
      </div>

      {/* Question */}
      <div className="space-y-1">
        <Label htmlFor="rfi-hs-question">Question</Label>
        <Textarea
          id="rfi-hs-question"
          placeholder="Describe the information needed to proceed work."
          rows={3}
          value={form.question}
          onChange={(e) => set('question', e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          The RFI will be saved as a draft. Submit it from the RFI list once the question is finalized.
        </p>
      </div>

      {/* Priority + Trade */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Priority</Label>
          <Select
            value={form.priority}
            onValueChange={(v) => set('priority', v as RFIPriority)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => (
                <SelectItem key={p} value={p}>
                  {RFI_PRIORITY_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Trade</Label>
          <CustomCombobox
            options={(trades ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={form.trade ?? ''}
            onChange={(v) => set('trade', v || null)}
            placeholder="Select trade"
            searchPlaceholder="Search trades…"
            addNewLabel="Add New Trade"
            emptyMessage="No trades found."
            onAddNew={handleAddNewTrade}
            AddNewDialog={AddNewTradeDialog}
          />
        </div>
      </div>

      {/* Responsible Party + Assignee */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Responsible Party</Label>
          <Select
            value={form.responsible_party_user != null ? String(form.responsible_party_user) : 'none'}
            onValueChange={(v) => setNullableUser('responsible_party_user', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {(projectUsers ?? []).map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {userDisplayName(u)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Assignee</Label>
          <Select
            value={form.assignee_user != null ? String(form.assignee_user) : 'none'}
            onValueChange={(v) => setNullableUser('assignee_user', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {(projectUsers ?? []).map((u) => (
                <SelectItem key={u.id} value={String(u.id)}>
                  {userDisplayName(u)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Due Date */}
      <div className="space-y-1">
        <Label htmlFor="rfi-hs-due">Due Date</Label>
        <Input
          id="rfi-hs-due"
          type="date"
          value={form.due_date}
          onChange={(e) => set('due_date', e.target.value)}
        />
      </div>

      {/* Cost + Schedule Impact */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Cost Impact</Label>
          <Select
            value={form.cost_impact}
            onValueChange={(v) => set('cost_impact', v as RFIImpact)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPACTS.map((i) => (
                <SelectItem key={i} value={i}>
                  {RFI_IMPACT_LABELS[i]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Schedule Impact</Label>
          <Select
            value={form.schedule_impact}
            onValueChange={(v) => set('schedule_impact', v as RFIImpact)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPACTS.map((i) => (
                <SelectItem key={i} value={i}>
                  {RFI_IMPACT_LABELS[i]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Suggested Solution */}
      <div className="space-y-1">
        <Label htmlFor="rfi-hs-suggested">Suggested Solution</Label>
        <Textarea
          id="rfi-hs-suggested"
          placeholder="Optional recommended approach from field team."
          rows={2}
          value={form.suggested_solution}
          onChange={(e) => set('suggested_solution', e.target.value)}
        />
      </div>

      {/* Attachments */}
      <div className="space-y-2">
        <Label>Attachments</Label>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
            Attach Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
        {attachmentPreviews.length > 0 && (
          <AttachmentPhotoGallery attachments={attachmentPreviews} maxVisible={6} onDelete={removeFileById} />
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create RFI
        </Button>
      </div>
    </form>
  );
};
